/**
 * AOCTANT Native Cart
 * Shipping estimator only.
 * Horizon owns all core cart behavior.
 */

(() => {
  'use strict';

  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  function updateProvinces(countrySelect) {
    const form = countrySelect.closest('[data-aoctant-cart-shipping-form]');
    if (!form) return;

    const wrapper = form.querySelector('[data-aoctant-cart-shipping-province-wrapper]');
    const select = form.querySelector('[data-aoctant-cart-shipping-province]');
    if (!wrapper || !select) return;

    const option = countrySelect.options[countrySelect.selectedIndex];
    const raw = option?.dataset?.provinces;

    select.replaceChildren();

    if (!raw || raw === '[]') {
      wrapper.hidden = true;
      return;
    }

    try {
      const provinces = JSON.parse(raw);

      if (!Array.isArray(provinces) || provinces.length === 0) {
        wrapper.hidden = true;
        return;
      }

      provinces.forEach(([value, label]) => {
        const optionElement = document.createElement('option');
        optionElement.value = value;
        optionElement.textContent = label;
        select.appendChild(optionElement);
      });

      wrapper.hidden = false;
    } catch {
      wrapper.hidden = true;
    }
  }

  function getAddress(form) {
    return {
      country:
        form.querySelector('[data-aoctant-cart-shipping-country]')?.value || '',
      province:
        form.querySelector('[data-aoctant-cart-shipping-province]')?.value || '',
      zip:
        form.querySelector('[data-aoctant-cart-shipping-zip]')?.value.trim() || '',
    };
  }

  function buildParams({ country, province, zip }) {
    const params = new URLSearchParams();

    params.set('shipping_address[country]', country);
    params.set('shipping_address[zip]', zip);

    if (province) {
      params.set('shipping_address[province]', province);
    }

    return params;
  }

  function showMessage(container, message, type = '') {
    container.replaceChildren();

    const p = document.createElement('p');
    p.className = `aoctant-cart-shipping-form__message${
      type ? ` aoctant-cart-shipping-form__message--${type}` : ''
    }`;
    p.textContent = message;

    container.appendChild(p);
  }

  function showRates(container, rates) {
    container.replaceChildren();

    const heading = document.createElement('p');
    heading.className =
      'aoctant-cart-shipping-form__message aoctant-cart-shipping-form__message--success';
    heading.textContent = 'Available shipping rates:';

    const list = document.createElement('ul');
    list.className = 'aoctant-cart-shipping-form__rates';

    rates.forEach((rate) => {
      const item = document.createElement('li');

      const name = document.createElement('span');
      name.textContent = rate.name || rate.presentment_name || 'Shipping';

      const price = document.createElement('span');
      price.textContent = rate.price || '';

      item.append(name, price);
      list.appendChild(item);
    });

    container.append(heading, list);
  }

  async function fetchShippingRates(address) {
    const root = window.Shopify?.routes?.root || '/';
    const params = buildParams(address);

    const prepare = await fetch(
      `${root}cart/prepare_shipping_rates.json?${params.toString()}`,
      {
        method: 'POST',
        headers: { Accept: 'application/json' },
      }
    );

    if (!prepare.ok) {
      let message = 'Unable to calculate shipping.';

      try {
        const data = await prepare.json();
        message = data?.message || data?.error || message;
      } catch {}

      throw new Error(message);
    }

    for (let attempt = 0; attempt < 10; attempt += 1) {
      await sleep(500);

      const response = await fetch(
        `${root}cart/async_shipping_rates.json?${params.toString()}`,
        {
          headers: { Accept: 'application/json' },
        }
      );

      if (!response.ok) {
        throw new Error('Unable to calculate shipping.');
      }

      const data = await response.json();

      if (Array.isArray(data?.shipping_rates)) {
        return data.shipping_rates;
      }
    }

    throw new Error(
      'Shipping rates are taking longer than expected. Please try again.'
    );
  }

  drawer.addEventListener('change', (event) => {
    const country = event.target.closest(
      '[data-aoctant-cart-shipping-country]'
    );

    if (country) updateProvinces(country);
  });

  drawer.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-aoctant-cart-shipping-form]');
    if (!form) return;

    event.preventDefault();

    const result = form.querySelector(
      '[data-aoctant-cart-shipping-result]'
    );
    if (!result) return;

    const address = getAddress(form);

    if (!address.country || !address.zip) {
      showMessage(result, 'Enter a country and postal code.', 'error');
      return;
    }

    const button = form.querySelector('button[type="submit"]');

    if (button) button.disabled = true;
    showMessage(result, 'Calculating shipping…');

    try {
      const rates = await fetchShippingRates(address);

      if (!rates.length) {
        showMessage(
          result,
          'No shipping rates are available for this location.',
          'error'
        );
      } else {
        showRates(result, rates);
      }
    } catch (error) {
      showMessage(
        result,
        error instanceof Error
          ? error.message
          : 'Unable to calculate shipping.',
        'error'
      );
    } finally {
      if (button) button.disabled = false;
    }
  });

  /* AOCTANT desktop quantity stepper.
     Visual buttons delegate to Horizon's native cart quantity controls. */
  drawer.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const increase = event.target.closest('[data-aoctant-quantity-increase]');
    const decrease = event.target.closest('[data-aoctant-quantity-decrease]');

    if (!increase && !decrease) return;

    const quantity = event.target.closest('[data-aoctant-cart-quantity]');
    if (!quantity) return;

    const nativeButton = quantity.querySelector(
      increase ? '.quantity-plus' : '.quantity-minus'
    );

    if (nativeButton instanceof HTMLButtonElement && !nativeButton.disabled) {
      nativeButton.click();
    }
  });

  /* AOCTANT utility buttons -> existing Horizon accordion panels */
  const utilityPanels = {
    note: '.cart-note details',
    shipping: '.aoctant-native-shipping details',
    discount: '.cart-discount details',
  };

  drawer.addEventListener('click', (event) => {
    const button = event.target.closest('[data-aoctant-native-cart-panel]');
    if (!button) return;

    const panelName = button.dataset.aoctantNativeCartPanel;
    const selector = utilityPanels[panelName];
    if (!selector) return;

    const targetDetails = drawer.querySelector(selector);
    const targetSummary = targetDetails?.querySelector('summary');
    if (!targetDetails || !targetSummary) return;

    Object.entries(utilityPanels).forEach(([name, panelSelector]) => {
      if (name === panelName) return;

      const details = drawer.querySelector(panelSelector);
      if (details?.open) {
        details.querySelector('summary')?.click();
      }
    });

    utilityReturnFocus = button;
    targetDetails.setAttribute('role', 'dialog');
    targetDetails.setAttribute('aria-modal', 'true');
    targetDetails.setAttribute(
      'aria-label',
      button.textContent.trim()
    );
    targetSummary.click();

    requestAnimationFrame(() => {
      syncUtilitySheetState();
      targetSummary.focus();

      drawer
        .querySelectorAll('[data-aoctant-native-cart-panel]')
        .forEach((utilityButton) => {
          const name = utilityButton.dataset.aoctantNativeCartPanel;
          const details = drawer.querySelector(utilityPanels[name]);

          utilityButton.setAttribute(
            'aria-expanded',
            details?.open ? 'true' : 'false'
          );
        });
    });
  });
  /* Turn the existing Horizon accordion into an AOCTANT visual sheet. */
  const utilityCloseDuration = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches
    ? 0
    : 600;

  let utilityCloseTimer = null;
  let utilityReturnFocus = null;

  const utilityFocusableSelector = [
    'summary',
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function getUtilityFocusables(details) {
    return Array.from(
      details.querySelectorAll(utilityFocusableSelector)
    ).filter((element) => element.getClientRects().length > 0);
  }

  function syncUtilityButtonState() {
    drawer
      .querySelectorAll('[data-aoctant-native-cart-panel]')
      .forEach((utilityButton) => {
        const name = utilityButton.dataset.aoctantNativeCartPanel;
        const details = drawer.querySelector(utilityPanels[name]);

        utilityButton.setAttribute(
          'aria-expanded',
          details?.open ? 'true' : 'false'
        );
      });
  }

  function syncUtilitySheetState() {
    const actions = drawer.querySelector('.cart-actions');
    if (!actions) return;

    if (
      actions.classList.contains(
        'aoctant-native-cart-sheet-closing'
      )
    ) {
      actions.classList.add('aoctant-native-cart-sheet-open');
      return;
    }

    const openDetails = actions.querySelector('details[open]');

    actions.classList.toggle(
      'aoctant-native-cart-sheet-open',
      Boolean(openDetails)
    );

    syncUtilityButtonState();
  }

  function closeUtilitySheet(details, onClosed) {
    const actions = drawer.querySelector('.cart-actions');

    if (!actions || !details?.open) {
      onClosed?.();
      return;
    }

    if (
      actions.classList.contains(
        'aoctant-native-cart-sheet-closing'
      )
    ) {
      return;
    }

    actions.classList.add(
      'aoctant-native-cart-sheet-open',
      'aoctant-native-cart-sheet-closing'
    );

    clearTimeout(utilityCloseTimer);

    utilityCloseTimer = window.setTimeout(() => {
      const returnFocus = utilityReturnFocus;
      utilityReturnFocus = null;

      details.open = false;
      details.removeAttribute('role');
      details.removeAttribute('aria-modal');
      details.removeAttribute('aria-label');

      actions.classList.remove(
        'aoctant-native-cart-sheet-closing'
      );

      syncUtilitySheetState();

      if (onClosed) {
        onClosed();
      } else if (returnFocus?.isConnected) {
        returnFocus.focus();
      }
    }, utilityCloseDuration);
  }

  /*
   * Intercept CLOSE actions before <details> collapses.
   * This keeps the panel present during its 600ms exit animation.
   */
  drawer.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const actions = drawer.querySelector('.cart-actions');
      if (!actions) return;

      const openDetails = actions.querySelector('details[open]');
      if (!openDetails) return;

      const utilityButton = event.target.closest(
        '[data-aoctant-native-cart-panel]'
      );

      if (utilityButton) {
        const panelName =
          utilityButton.dataset.aoctantNativeCartPanel;
        const targetDetails = drawer.querySelector(
          utilityPanels[panelName]
        );

        if (targetDetails === openDetails) {
          event.preventDefault();
          event.stopImmediatePropagation();

          closeUtilitySheet(openDetails);
          return;
        }

        if (targetDetails && targetDetails !== openDetails) {
          event.preventDefault();
          event.stopImmediatePropagation();

          closeUtilitySheet(openDetails, () => {
            utilityButton.click();
          });

          return;
        }
      }

      const openSummary = event.target.closest(
        '.cart-actions details[open] > summary'
      );

      if (openSummary && actions.contains(openSummary)) {
        event.preventDefault();
        event.stopImmediatePropagation();

        closeUtilitySheet(openSummary.parentElement);
        return;
      }

      if (event.target === actions) {
        event.preventDefault();
        event.stopImmediatePropagation();

        closeUtilitySheet(openDetails);
      }
    },
    true
  );

  /* Sync opening state after Horizon opens the native details. */
  drawer.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    if (
      event.target.closest(
        '[data-aoctant-native-cart-panel]'
      ) ||
      event.target.closest('.cart-actions summary')
    ) {
      requestAnimationFrame(syncUtilitySheetState);
    }
  });

  drawer.addEventListener('keydown', (event) => {
    const actions = drawer.querySelector('.cart-actions');
    const openDetails = actions?.querySelector('details[open]');

    if (!openDetails) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeUtilitySheet(openDetails);
      return;
    }

    if (event.key !== 'Tab') return;

    const focusables = getUtilityFocusables(openDetails);
    const firstFocusable = focusables[0];
    const lastFocusable = focusables.at(-1);

    if (!firstFocusable || !lastFocusable) return;

    if (!openDetails.contains(document.activeElement)) {
      event.preventDefault();
      firstFocusable.focus();
      return;
    }

    if (event.shiftKey && document.activeElement === firstFocusable) {
      event.preventDefault();
      lastFocusable.focus();
    } else if (!event.shiftKey && document.activeElement === lastFocusable) {
      event.preventDefault();
      firstFocusable.focus();
    }
  }, true);


  /* AOCTANT cart-note apply:
     trigger Horizon's native note update, then use our
     existing forensic 600ms nested-sheet close. */
  drawer.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const applyButton = event.target.closest(
      '[data-aoctant-cart-note-apply]'
    );

    if (!applyButton) return;

    const cartNote = applyButton.closest('cart-note');
    const textarea = cartNote?.querySelector(
      'textarea[name="note"]'
    );
    const details = applyButton.closest('details');

    if (
      !(textarea instanceof HTMLTextAreaElement) ||
      !(details instanceof HTMLDetailsElement)
    ) {
      return;
    }

    /* Uses Horizon cart-note.js /updateCartNote. */
    textarea.dispatchEvent(
      new Event('input', { bubbles: true })
    );

    closeUtilitySheet(details);
  });
  syncUtilitySheetState();
})();

/* =========================================================
   AOCTANT cart drawer tabs + accessories panel
   Native Horizon cart behavior is untouched.
   ========================================================= */

(function initAoctantCartTabs() {
  'use strict';

  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;

  const TAB_NAME = 'data-aoctant-native-cart-tab';
  const PANEL_IDS = {
    cart: 'aoctant-native-cart-cart-panel',
    accessories: 'aoctant-native-cart-accessories-panel',
  };

  function getTabs() {
    return Array.from(drawer.querySelectorAll('[' + TAB_NAME + ']'));
  }

  function getPanels() {
    return {
      cart: drawer.querySelector('#' + PANEL_IDS.cart),
      accessories: drawer.querySelector('#' + PANEL_IDS.accessories),
    };
  }

  function getActiveTabName() {
    const activeTab = getTabs().find(
      (tab) => tab.getAttribute('aria-selected') === 'true'
    );
    return activeTab?.getAttribute(TAB_NAME) ?? 'cart';
  }

  function activateTab(targetName) {
    if (targetName === getActiveTabName()) return;

    const tabs = getTabs();
    const panels = getPanels();

    tabs.forEach((tab) => {
      const selected = tab.getAttribute(TAB_NAME) === targetName;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    });

    Object.entries(panels).forEach(([name, panel]) => {
      if (!panel) return;
      panel.hidden = name !== targetName;
    });
  }

  drawer.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[' + TAB_NAME + ']');
    if (!tab) return;

    const targetName = tab.getAttribute(TAB_NAME);
    if (targetName === getActiveTabName()) return;

    event.preventDefault();
    activateTab(targetName);
  });

  drawer.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.aoctant-native-cart-header__tabs')) return;

    const tabs = getTabs();
    const activeIndex = tabs.findIndex(
      (tab) => tab.getAttribute('aria-selected') === 'true'
    );
    if (activeIndex === -1) return;

    event.preventDefault();

    const nextIndex =
      event.key === 'ArrowRight'
        ? Math.min(activeIndex + 1, tabs.length - 1)
        : Math.max(activeIndex - 1, 0);

    const nextTab = tabs[nextIndex];
    activateTab(nextTab.getAttribute(TAB_NAME));
    nextTab.focus();
  });
})();

/* =========================================================
   AOCTANT cart complementary carousel
   Loaded via Shopify Product Recommendations Section Rendering.
   ========================================================= */

class AoctantCartComplementary extends HTMLElement {
  #fetchController = null;
  #listeners = null;
  #resizeObserver = null;

  connectedCallback() {
    this.load();
  }

  disconnectedCallback() {
    this.#fetchController?.abort();
    this.#listeners?.abort();
    this.#resizeObserver?.disconnect();
  }

  async load() {
    const productId = this.dataset.productId;
    if (!productId) return;

    const root = window.Shopify?.routes?.root || '/';
    const sectionId = this.dataset.sectionId || 'aoctant-cart-complementary-render';
    const url = root +
      'recommendations/products?product_id=' + encodeURIComponent(productId) +
      '&section_id=' + encodeURIComponent(sectionId) +
      '&limit=5&intent=complementary';

    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    try {
      const response = await fetch(url, {
        signal: this.#fetchController.signal,
      });

      if (!response.ok) return;

      const html = await response.text();
      if (!html.trim()) {
        this.innerHTML = '';
        this.hidden = true;
        return;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const rendered = doc.querySelector('[data-aoctant-cart-complementary-rendered]');

      if (!rendered) {
        this.innerHTML = '';
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.innerHTML = '';
      this.appendChild(rendered);
      this.#initCarousel();
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[aoctant-cart-complementary] load failed:', error);
      }
    }
  }

  #initCarousel() {
    const viewport = this.querySelector('.aoctant-cart-complementary__viewport');
    const track = this.querySelector('.aoctant-cart-complementary__track');
    const prev = this.querySelector('.aoctant-cart-complementary__prev');
    const next = this.querySelector('.aoctant-cart-complementary__next');

    if (!viewport || !track || !prev || !next) return;

    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    const slides = Array.from(track.children);
    const getSlideWidth = () => {
      const slide = slides[0];
      if (!slide) return 0;
      const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      return slide.offsetWidth + gap;
    };

    const nearestIndex = () => {
      const slideWidth = getSlideWidth();
      if (!slideWidth) return 0;
      return Math.round(viewport.scrollLeft / slideWidth);
    };

    const scrollToIndex = (index) => {
      const slideWidth = getSlideWidth();
      if (!slideWidth) return;
      const maxIndex = Math.max(0, slides.length - 1);
      const targetIndex = Math.max(0, Math.min(index, maxIndex));
      viewport.scrollTo({
        left: targetIndex * slideWidth,
        behavior: 'smooth',
      });
    };

    const updateButtons = () => {
      const slideWidth = getSlideWidth();
      const index = slideWidth ? Math.round(viewport.scrollLeft / slideWidth) : 0;
      prev.disabled = index <= 0;
      next.disabled = index >= slides.length - 1 || viewport.clientWidth >= viewport.scrollWidth;
    };

    const scrollByDirection = (direction) => {
      scrollToIndex(nearestIndex() + direction);
    };

    prev.addEventListener('click', () => scrollByDirection(-1), { signal });
    next.addEventListener('click', () => scrollByDirection(1), { signal });
    viewport.addEventListener('scroll', updateButtons, { passive: true, signal });
    updateButtons();

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateButtons);
    });
    this.#resizeObserver.observe(viewport);

    let isDragging = false;
    let startX = 0;
    let scrollStart = 0;

    viewport.addEventListener('pointerdown', (event) => {
      if (event.target.closest('button, a, input, select, textarea, label')) return;

      isDragging = true;
      startX = event.clientX;
      scrollStart = viewport.scrollLeft;
      viewport.setPointerCapture(event.pointerId);
      viewport.classList.add('aoctant-cart-complementary__viewport--dragging');
    }, { signal });

    viewport.addEventListener('pointermove', (event) => {
      if (!isDragging) return;
      event.preventDefault();
      const delta = event.clientX - startX;
      viewport.scrollLeft = scrollStart - delta;
    }, { signal });

    const endDrag = (event) => {
      if (!isDragging) return;
      isDragging = false;
      if (event?.pointerId) {
        viewport.releasePointerCapture(event.pointerId);
      }
      viewport.classList.remove('aoctant-cart-complementary__viewport--dragging');
      scrollToIndex(nearestIndex());
    };

    viewport.addEventListener('pointerup', endDrag, { signal });
    viewport.addEventListener('pointercancel', endDrag, { signal });
  }
}

if (!customElements.get('aoctant-cart-complementary')) {
  customElements.define('aoctant-cart-complementary', AoctantCartComplementary);
}
/* =========================================================
   AOCTANT cart desktop outside-click close
   Horizon already owns modal backdrop closing below 990px.
   This fills only the desktop non-modal sidebar gap.
   ========================================================= */
(function initAoctantCartDesktopOutsideClose() {
  'use strict';

  if (window.__aoctantCartDesktopOutsideCloseBound) return;
  window.__aoctantCartDesktopOutsideCloseBound = true;

  const desktopQuery = window.matchMedia('(min-width: 990px)');

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (!desktopQuery.matches) return;
      if (event.button !== 0) return;
      if (!(event.target instanceof Element)) return;

      const drawer = document.getElementById('cart-drawer');
      if (!drawer || !drawer.hasAttribute('open')) return;

      /* Do not interfere with the cart trigger itself. */
      if (event.target.closest('[aria-controls="cart-drawer"]')) return;

      const panel = drawer.querySelector('.theme-drawer__dialog');
      if (!(panel instanceof HTMLDialogElement) || !panel.open) return;

      /* Inside the drawer is never an outside click. */
      if (panel.contains(event.target)) return;

      /* Never close the parent while Note / Shipping / Discount is open. */
      const isOuterBackdrop = Boolean(
        event.target.closest('[data-aoctant-cart-drawer-backdrop]')
      );
      if (
        !isOuterBackdrop
        && drawer.querySelector('.cart-actions.aoctant-native-cart-sheet-open')
      ) return;

      if (typeof drawer.close === 'function') {
        drawer.close();
      }
    },
    true
  );
})();

/* =========================================================
   AOCTANT cart mobile swipe-down dismissal
   Horizon remains responsible for the actual close lifecycle.
   ========================================================= */
(function initAoctantCartMobileSwipeClose() {
  'use strict';

  if (window.__aoctantCartMobileSwipeCloseBound) return;

  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;

  window.__aoctantCartMobileSwipeCloseBound = true;

  const mobileQuery = window.matchMedia('(max-width: 639px)');
  const interactiveSelector = 'button, a, input, select, textarea, label, summary, [role="button"]';
  const axisLockDistance = 8;
  const closeDistance = 80;
  const closeHeightRatio = 0.18;
  const resetDuration = 280;
  let gesture = null;
  let resetTimer = 0;
  let resetTransitionHandler = null;

  const clearResetTimer = () => {
    if (!resetTimer) return;
    window.clearTimeout(resetTimer);
    resetTimer = 0;
  };

  const clearPanelVisuals = (panel) => {
    clearResetTimer();
    if (resetTransitionHandler) {
      panel.removeEventListener('transitionend', resetTransitionHandler);
      resetTransitionHandler = null;
    }
    panel.classList.remove(
      'aoctant-native-cart-swipe-active',
      'aoctant-native-cart-swipe-resetting'
    );
    panel.style.removeProperty('transform');
    panel.style.removeProperty('--aoctant-cart-swipe-offset');
  };

  const releaseCapture = (currentGesture) => {
    if (!currentGesture?.header.hasPointerCapture(currentGesture.pointerId)) return;
    currentGesture.header.releasePointerCapture(currentGesture.pointerId);
  };

  const finishGesture = () => {
    const currentGesture = gesture;
    gesture = null;
    if (!currentGesture) return null;

    try {
      releaseCapture(currentGesture);
    } catch {}

    return currentGesture;
  };

  const resetPanel = (currentGesture) => {
    const { panel, travel } = currentGesture;
    if (travel <= 0) {
      clearPanelVisuals(panel);
      return;
    }

    clearResetTimer();
    panel.classList.remove('aoctant-native-cart-swipe-active');
    panel.classList.add('aoctant-native-cart-swipe-resetting');
    panel.style.transform = `translate3d(0, ${travel}px, 0)`;

    requestAnimationFrame(() => {
      panel.style.transform = 'translate3d(0, 0, 0)';
    });

    const finishReset = () => clearPanelVisuals(panel);
    resetTransitionHandler = (event) => {
      if (event.target === panel && event.propertyName === 'transform') finishReset();
    };
    panel.addEventListener('transitionend', resetTransitionHandler);
    resetTimer = window.setTimeout(finishReset, resetDuration + 80);
  };

  const cancelGesture = () => {
    const currentGesture = finishGesture();
    if (currentGesture) resetPanel(currentGesture);
  };

  drawer.addEventListener('pointerdown', (event) => {
    if (!mobileQuery.matches || gesture || !event.isPrimary) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (!(event.target instanceof Element)) return;

    const header = event.target.closest('.theme-drawer__header');
    if (!header || !drawer.contains(header)) return;
    if (event.target.closest(interactiveSelector)) return;
    if (drawer.querySelector('.cart-actions.aoctant-native-cart-sheet-open')) return;

    const panel = drawer.querySelector('.theme-drawer__dialog');
    if (!(panel instanceof HTMLDialogElement) || !panel.open) return;
    if (panel.classList.contains('theme-drawer__dialog--closing')) return;

    clearPanelVisuals(panel);
    gesture = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      travel: 0,
      direction: 'pending',
      header,
      panel,
    };

    try {
      header.setPointerCapture(event.pointerId);
    } catch {}
  });

  drawer.addEventListener('pointermove', (event) => {
    if (!gesture || event.pointerId !== gesture.pointerId) return;

    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;
    const absX = Math.abs(deltaX);

    if (gesture.direction === 'pending') {
      if (Math.max(absX, Math.abs(deltaY)) < axisLockDistance) return;
      if (deltaY <= 0 || absX > deltaY) {
        cancelGesture();
        return;
      }
      gesture.direction = 'vertical';
      gesture.panel.classList.add('aoctant-native-cart-swipe-active');
    }

    if (deltaY < 0 || absX > deltaY) {
      cancelGesture();
      return;
    }

    event.preventDefault();
    gesture.travel = deltaY;
    gesture.panel.style.transform = `translate3d(0, ${deltaY}px, 0)`;
  });

  drawer.addEventListener('pointerup', (event) => {
    if (!gesture || event.pointerId !== gesture.pointerId) return;

    const currentGesture = finishGesture();
    if (!currentGesture) return;

    const sheetHeight = currentGesture.panel.getBoundingClientRect().height;
    const shouldClose =
      currentGesture.direction === 'vertical' &&
      (currentGesture.travel >= closeDistance ||
        currentGesture.travel >= sheetHeight * closeHeightRatio);

    if (!shouldClose) {
      resetPanel(currentGesture);
      return;
    }

    clearResetTimer();
    currentGesture.panel.style.setProperty(
      '--aoctant-cart-swipe-offset',
      `${currentGesture.travel}px`
    );
    currentGesture.panel.classList.remove(
      'aoctant-native-cart-swipe-active',
      'aoctant-native-cart-swipe-resetting'
    );
    currentGesture.panel.style.removeProperty('transform');

    if (typeof drawer.close === 'function') {
      drawer.close();
      window.setTimeout(() => {
        currentGesture.panel.style.removeProperty('--aoctant-cart-swipe-offset');
      }, 700);
    } else {
      resetPanel(currentGesture);
    }
  });

  drawer.addEventListener('pointercancel', cancelGesture);
  window.addEventListener('blur', cancelGesture);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) cancelGesture();
  });
  mobileQuery.addEventListener('change', (event) => {
    if (!event.matches) cancelGesture();
  });
})();

/* Empty-cart navigation remains native; this only dismisses the open drawer. */
(function initAoctantEmptyCartContinueClose() {
  'use strict';

  if (window.__aoctantEmptyCartContinueCloseBound) return;

  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;

  window.__aoctantEmptyCartContinueCloseBound = true;

  drawer.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a.aoctant-cart-empty-state__action');
    if (!link || !drawer.contains(link)) return;
    if (link.hasAttribute('download')) return;
    if (link.target && link.target.toLowerCase() !== '_self') return;

    if (typeof drawer.close === 'function') drawer.close();
  });
})();
